import { useState, useEffect } from 'react';
import './TrackerTab.css';
import TradingChart from './TradingChart';
import { useDexScreener, fetchByAddress, fmtDex } from './useDexScreener';
import { supabase } from './supabase';

const TOKENS = [
  { id: 1, symbol: 'SOL',    name: 'Wrapped SOL',  price: 0,        change: 0.0,  icon: 'S', iconBg: '#9945ff', address: 'So11111111111111111111111111111111111111112', badge: 'Clean Launch'},
  { id: 2, symbol: 'USDT',   name: 'Tether USD',   price: 1.0000,   change: 0.0,  icon: 'U', iconBg: '#26a17b', address: null },
  { id: 3, symbol: '???',    name: 'Unknown',       price: 0.001641, change: -0.9, icon: '?', iconBg: '#252525', address: null },
  { id: 4, symbol: 'PYUSD',  name: 'PayPal USD',    price: 1.0000,   change: 0.0,  icon: 'P', iconBg: '#003087', address: null },
  { id: 5, symbol: 'WAR',    name: 'War Token',     price: 0.001582, change: 0.0,  icon: '', iconBg: '#3a1010', address: null },
  { id: 6, symbol: 'fregly', name: 'fregly',        price: 0,        change: 0.0,  icon: 'f', iconBg: '#3a2a1a', address: null },
  { id: 7, symbol: 'BP',     name: 'Blueprint',     price: 6.3639,   change: 0.0,  icon: 'B', iconBg: '#1a3a5a', address: null },
];

const TIMEFRAMES = ['1s', '1m', '5m', '15m', '30m', '1H', '2H', '4H', '1D', '1W'];

const hashColor = (str = '') => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return `hsl(${Math.abs(h) % 360},40%,22%)`;
};

const fmtPrice = (p) => {
  if (!p) return '$—';
  if (p < 0.000001) return `$${p.toExponential(2)}`;
  if (p < 0.01)  return `$${p.toFixed(6)}`;
  if (p < 1)     return `$${p.toFixed(4)}`;
  if (p < 10)    return `$${p.toFixed(4)}`;
  return `$${p.toFixed(2)}`;
};
const fmtChange = (c) => `${c >= 0 ? '+': ''}${c.toFixed(1)}%`;

export default function TrackerTab({ solPrice = 0, solChange = 0, pendingAddr = null, onPendingHandled = () => {} }) {

  const [tokens, setTokens] = useState(() => {
    try {
      const saved = localStorage.getItem('tracker_tokens');
      return saved ? JSON.parse(saved) : TOKENS;
    } catch { return TOKENS; }
  });

  const [activeId, setActiveId] = useState(() => {
    try {
      return parseInt(localStorage.getItem('tracker_activeId')) || 1;
    } catch { return 1; }
  });

  const [timeframe,  setTimeframe]  = useState('1H');
  const [scale,      setScale]      = useState('Log');
  const [showSearch, setShowSearch] = useState(false);
  const [searchVal,  setSearchVal]  = useState('');
  const [searchErr,  setSearchErr]  = useState('');
  const [searching,  setSearching]  = useState(false);

  useEffect(() => {
    localStorage.setItem('tracker_tokens', JSON.stringify(tokens));
  }, [tokens]);

  useEffect(() => {
    localStorage.setItem('tracker_activeId', activeId);
  }, [activeId]);

  const active = tokens.find(t => t.id === activeId) || tokens[0];
  const dex = useDexScreener(active.symbol, active.pairAddress, active.chainId);

  const displayPrice  = dex?.price > 0 ? dex.price : (active.id === 1 && solPrice > 0 ? solPrice : active.price);
  const displayChange = dex?.change24h !== undefined ? dex.change24h : (active.id === 1 ? solChange : active.change);

  const dexSidebarUrl = dex?.pairAddress
    ? `https://dexscreener.com/solana/${dex.pairAddress}?embed=1&theme=dark&info=1&chart=0&trades=0`: active.address
    ? `https://dexscreener.com/solana/${active.address}?embed=1&theme=dark&info=1&chart=0&trades=0`: null;

  useEffect(() => {
    if (dex?.price > 0) {
      setTokens(prev => prev.map(t =>
        t.id === activeId ? { ...t, price: dex.price, change: dex.change24h } : t
      ));
    }
  }, [dex, activeId]);

  useEffect(() => {
    if (!pendingAddr) return;
    onPendingHandled();
    addByAddress(pendingAddr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAddr]);

  useEffect(() => {
    if (solPrice > 0) {
      setTokens(prev => prev.map(t =>
        t.id === 1 && !(t.price > 0) ? { ...t, price: solPrice, change: solChange } : t
      ));
    }
  }, [solPrice, solChange]);

  const addByAddress = async (addr) => {
    const trimmed = addr.trim();
    if (!trimmed) { setSearchErr('Entre une adresse ou un symbole'); return; }
    setSearching(true);
    setSearchErr('');
    try {
      const found = await fetchByAddress(trimmed);
      if (!found) { setSearchErr('Token introuvable sur Solana'); return; }
      const exists = tokens.find(t => t.address?.toLowerCase() === found.address?.toLowerCase());
      if (exists) {
        setActiveId(exists.id);
        setShowSearch(false); setSearchVal('');
        return;
      }
      const newTok = {
        id:          Date.now(),
        symbol:      found.symbol,
        name:        found.name,
        price:       found.price,
        change:      found.change24h,
        icon:        found.symbol.charAt(0).toUpperCase(),
        iconBg:      hashColor(found.address),
        address:     found.address,
        pairAddress: found.pairAddress,
        chainId:     found.chainId,
        imageUrl:    found.imageUrl || null,
      };
      setTokens(prev => [...prev, newTok]);
      setActiveId(newTok.id);
      setShowSearch(false); setSearchVal('');

      //  Sauvegarder dans Supabase
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          await supabase.from('wallet_history').insert({
            user_id: session.user.id,
            wallet_address: found.address,
          });
        }
      } catch(e) { console.warn('wallet_history save error:', e); }

    } catch (e) {
      console.error('addByAddress:', e);
      setSearchErr(e.message || 'Erreur réseau');
    } finally {
      setSearching(false);
    }
  };

  const removeToken = (id, e) => {
    e.stopPropagation();
    if (tokens.length === 1) return;
    const next = tokens.filter(t => t.id !== id);
    setTokens(next);
    if (activeId === id) setActiveId(next[0].id);
  };

  const totalTxns = (dex?.buys24h || 0) + (dex?.sells24h || 0);
  const buyPct  = totalTxns > 0 ? Math.round((dex.buys24h  / totalTxns) * 100) : 50;
  const sellPct = 100 - buyPct;

  return (
    <div className="tk-root">

      {/* Pills bar */}
      <div className="tk-bar">
        <div className="tk-bar-label">
          <svg viewBox="0 0 12 12"fill="none"width="10"height="10">
            <circle cx="6"cy="6"r="4.5"stroke="currentColor"strokeWidth="1.2"/>
            <path d="M6 3.5v2.5l1.5 1"stroke="currentColor"strokeWidth="1.2"strokeLinecap="round"/>
          </svg>
          RÉCENT
        </div>
        <div className="tk-pills">
          {tokens.map(t => (
            <div key={t.id} className={`tk-pill ${activeId === t.id ? 'active': ''}`} onClick={() => setActiveId(t.id)}>
              <div className="tk-pill-icon"style={{ background: t.iconBg }}>{t.icon}</div>
              <span className="tk-pill-sym">{t.symbol}</span>
              <span className="tk-pill-price">{fmtPrice(t.price)}</span>
              <span className={`tk-pill-chg ${t.change >= 0 ? 'pos': 'neg'}`}>{fmtChange(t.change)}</span>
              {activeId === t.id && (
                <button className="tk-pill-x"onClick={e => removeToken(t.id, e)}></button>
              )}
            </div>
          ))}
          {showSearch ? (
            <div className="tk-search-wrap">
              <input
                className="tk-search-input"placeholder="Coller l'adresse du contrat Solana…"value={searchVal}
                onChange={e => { setSearchVal(e.target.value); setSearchErr(''); }}
                onKeyDown={e => e.key === 'Enter'&& addByAddress(e.target.value)}
                autoFocus
              />
              {searching && <span className="tk-search-status">Recherche…</span>}
              {searchErr  && <span className="tk-search-err">{searchErr}</span>}
              <button className="tk-search-ok"onClick={() => addByAddress(searchVal)} disabled={searching}>↵</button>
              <button className="tk-search-cancel"onClick={() => { setShowSearch(false); setSearchVal(''); setSearchErr(''); }}></button>
            </div>
          ) : (
            <button className="tk-pill-more"onClick={() => setShowSearch(true)} title="Ajouter un token">
              <svg viewBox="0 0 12 12"fill="none"width="11"height="11">
                <path d="M6 2v8M2 6h8"stroke="currentColor"strokeWidth="1.4"strokeLinecap="round"/>
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Token header */}
      <div className="tk-token-hd">
        <div className="tk-th-left">
          <div className="tk-th-icon"style={{ background: active.iconBg }}>{active.icon}</div>
          <div className="tk-th-info">
            <div className="tk-th-name-row">
              <span className="tk-th-sym">{active.symbol}</span>
              <span className="tk-th-name">{active.name}</span>
              {active.badge && <span className="tk-launch-badge">{active.badge}</span>}
            </div>
            {active.address && (
              <div className="tk-th-addr">
                {active.address}
                <button className="tk-copy-btn"title="Copier l'adresse"onClick={() => navigator.clipboard?.writeText(active.address)}>
                  <svg viewBox="0 0 12 12"fill="none"width="10"height="10">
                    <rect x="4"y="4"width="7"height="7"rx="1"stroke="currentColor"strokeWidth="1.1"/>
                    <path d="M1 8V2a1 1 0 0 1 1-1h6"stroke="currentColor"strokeWidth="1.1"strokeLinecap="round"/>
                  </svg>
                </button>
              </div>
            )}
          </div>
          <div className="tk-th-price-block">
            <span className="tk-th-price">{fmtPrice(displayPrice)}</span>
            <span className={`tk-th-chg ${displayChange >= 0 ? 'pos': 'neg'}`}>{fmtChange(displayChange)}</span>
          </div>
          <div className="tk-th-stats">
            <div className="tk-th-stat"><span className="tk-th-sl">MCAP</span><span className="tk-th-sv">{fmtDex(dex?.mcap)}</span></div>
            <div className="tk-th-stat"><span className="tk-th-sl">LIQ</span><span className="tk-th-sv">{fmtDex(dex?.liquidity)}</span></div>
            <div className="tk-th-stat"><span className="tk-th-sl">VOL 24H</span><span className="tk-th-sv">{fmtDex(dex?.volume24h)}</span></div>
          </div>
        </div>
        <button className="tk-solscan-btn"onClick={() => active.address && window.open(`https://solscan.io/token/${active.address}`, '_blank')}>
          <svg viewBox="0 0 12 12"fill="none"width="11"height="11">
            <path d="M6 2H2v8h8V6M9 1h2v2M11 1L6 6"stroke="currentColor"strokeWidth="1.2"strokeLinecap="round"strokeLinejoin="round"/>
          </svg>
          Solscan
        </button>
      </div>

      {/* Main body */}
      <div className="tk-body">
        <div className="tk-chart-col">
          <div className="tk-controls">
            <div className="tk-ctrl-left">
              <button className="tk-ctrl-btn active"title="Chandeliers">
                <svg viewBox="0 0 16 16"fill="none"width="12"height="12">
                  <rect x="2"y="5"width="3"height="5"rx=".5"fill="currentColor"/>
                  <line x1="3.5"y1="3"x2="3.5"y2="5"stroke="currentColor"strokeWidth="1.2"/>
                  <line x1="3.5"y1="10"x2="3.5"y2="13"stroke="currentColor"strokeWidth="1.2"/>
                  <rect x="7"y="6"width="3"height="4"rx=".5"fill="currentColor"opacity=".8"/>
                  <line x1="8.5"y1="4"x2="8.5"y2="6"stroke="currentColor"strokeWidth="1.2"/>
                  <line x1="8.5"y1="10"x2="8.5"y2="12"stroke="currentColor"strokeWidth="1.2"/>
                  <rect x="12"y="7"width="3"height="3"rx=".5"fill="currentColor"opacity=".6"/>
                  <line x1="13.5"y1="5"x2="13.5"y2="7"stroke="currentColor"strokeWidth="1.2"/>
                  <line x1="13.5"y1="10"x2="13.5"y2="11.5"stroke="currentColor"strokeWidth="1.2"/>
                </svg>
              </button>
              <button className="tk-ctrl-btn"title="Ligne">
                <svg viewBox="0 0 16 16"fill="none"width="12"height="12">
                  <path d="M2 12l3-4 3 2 5-7"stroke="currentColor"strokeWidth="1.3"strokeLinecap="round"strokeLinejoin="round"/>
                </svg>
              </button>
              <div className="tk-ctrl-sep"/>
              <div className="tk-tfs">
                {TIMEFRAMES.map(tf => (
                  <button key={tf} className={`tk-tf ${timeframe === tf ? 'active': ''}`} onClick={() => setTimeframe(tf)}>
                    {tf}
                  </button>
                ))}
              </div>
              <div className="tk-ctrl-sep"/>
              {['Lin', 'Log'].map(s => (
                <button key={s} className={`tk-scale ${scale === s ? 'active': ''}`} onClick={() => setScale(s)}>{s}</button>
              ))}
              <div className="tk-ctrl-sep"/>
              <button className="tk-ctrl-btn">
                <svg viewBox="0 0 14 14"fill="none"width="12"height="12">
                  <rect x="1"y="1"width="12"height="12"rx="1.5"stroke="currentColor"strokeWidth="1.2"/>
                  <line x1="1"y1="5"x2="13"y2="5"stroke="currentColor"strokeWidth="1.2"/>
                  <line x1="6"y1="5"x2="6"y2="13"stroke="currentColor"strokeWidth="1.2"/>
                </svg>
              </button>
            </div>
            <div className="tk-ctrl-right">
              <span className="tk-tz">UTC+2</span>
              <button className="tk-ctrl-btn tk-ctrl-text">MA</button>
              <button className="tk-ctrl-btn tk-ctrl-text">MC</button>
              <button className="tk-ctrl-btn">
                <svg viewBox="0 0 12 12"fill="none"width="11"height="11">
                  <circle cx="6"cy="6"r="4.5"stroke="currentColor"strokeWidth="1.2"/>
                  <path d="M6 4v1.5M6 7.5v.5"stroke="currentColor"strokeWidth="1.3"strokeLinecap="round"/>
                </svg>
              </button>
              <button className="tk-ctrl-btn">
                <svg viewBox="0 0 14 14"fill="none"width="12"height="12">
                  <path d="M5 2H2v3M12 2h-3M2 9v3h3M9 12h3V9"stroke="currentColor"strokeWidth="1.2"strokeLinecap="round"strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>

          <div className="tk-chart"style={{flex: 1}}>
            <TradingChart
              pairAddress={dex?.pairAddress}
              chainId={dex?.chainId || active.chainId}
              timeframe={timeframe}
            />
          </div>
        </div>

        <div className="tk-sidebar"style={{padding:0, overflow:'hidden'}}>
          {dexSidebarUrl ? (
            <iframe
              key={dexSidebarUrl}
              src={dexSidebarUrl}
              title="DexScreener Info"style={{width:'100%',height:'100%',border:'none',display:'block'}}
              allow="clipboard-write"/>
          ) : (
            <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%',color:'#3a4a5a',fontSize:12}}>
              Sélectionne un token
            </div>
          )}
        </div>
      </div>
    </div>
  );
}